import * as THREE from 'three';
import * as Astronomy from 'astronomy-engine';
import starGlowVert from '../shaders/starGlow.vert?raw';
import starGlowFrag from '../shaders/starGlow.frag?raw';

const SKY_RADIUS = 800;

/**
 * StarField - renders the real night sky from the HYG star catalog,
 * transformed to the observer's location and time.
 *
 * For Megha, this means: the sky as it appeared from Bangalore, Karnataka
 * at midnight IST on April 2, 1998 - the night she was born. Every star is
 * placed at its real horizontal coordinates (altitude/azimuth) computed via
 * the astronomy-engine library.
 *
 * Color comes from each star's B-V index (real spectral color).
 * Size comes from apparent magnitude (real brightness).
 */
export class StarField {
  constructor({ hyg, birthplace, birthDate }) {
    this.catalog = hyg;
    this.birthplace = birthplace;
    this.birthDate = birthDate;

    // Set up the astronomy-engine observer at her birthplace
    this.observer = new Astronomy.Observer(birthplace.lat, birthplace.lon, 920);
    this.time = new Astronomy.AstroTime(birthDate);

    const count = hyg.length;
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    const sizes = new Float32Array(count);

    for (let i = 0; i < count; i++) {
      const s = hyg[i];

      // Compute the star's horizontal position (azimuth + altitude) from
      // the observer at her birthplace at the moment of her birth
      const pos = this._horizontalToCartesian(s.ra, s.dec);

      positions[i * 3 + 0] = pos.x;
      positions[i * 3 + 1] = pos.y;
      positions[i * 3 + 2] = pos.z;

      // Color from B-V index
      const rgb = bvToRgb(s.ci);
      colors[i * 3 + 0] = rgb[0];
      colors[i * 3 + 1] = rgb[1];
      colors[i * 3 + 2] = rgb[2];

      // Size from apparent magnitude
      sizes[i] = magToSize(s.m);
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

    this.material = new THREE.ShaderMaterial({
      vertexShader: starGlowVert,
      fragmentShader: starGlowFrag,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });

    this.mesh = new THREE.Points(geometry, this.material);
    this.mesh.renderOrder = -1;
  }

  /**
   * Compute the 3D position of a star using the same observer transform.
   * Used by interactive star modules (Sirius, BirthStar, etc.) to know
   * where to place their click-target mesh so it lines up with the
   * visible star in the StarField.
   */
  _horizontalToCartesian(ra, dec) {
    const horizon = Astronomy.Horizon(this.time, this.observer, ra, dec, 'normal');
    const azRad = (horizon.azimuth * Math.PI) / 180;
    const altRad = (horizon.altitude * Math.PI) / 180;
    const cosAlt = Math.cos(altRad);

    return {
      x: SKY_RADIUS * cosAlt * Math.sin(azRad),
      y: SKY_RADIUS * Math.sin(altRad),
      z: -SKY_RADIUS * cosAlt * Math.cos(azRad),
    };
  }

  /**
   * Get the 3D position for a star object from the catalog.
   * Returns a THREE.Vector3.
   */
  getStarPosition(star) {
    const pos = this._horizontalToCartesian(star.ra, star.dec);
    return new THREE.Vector3(pos.x, pos.y, pos.z);
  }

  /**
   * Look up a star by its proper name.
   * @example findByName('Sirius') → { n: 'Sirius', ra, dec, ... }
   */
  findByName(name) {
    return this.catalog.find((s) => s.n === name);
  }

  /**
   * Look up a star by its Bayer/Flamsteed designation.
   * Tries exact match first, then case-insensitive contains match.
   * @example findByBayer('40Eri') or findByBayer('Tau Cet')
   */
  findByBayer(bayerStr) {
    const exact = this.catalog.find((s) => s.bf === bayerStr);
    if (exact) return exact;
    const norm = bayerStr.toLowerCase().replace(/\s+/g, '');
    return this.catalog.find((s) => {
      if (!s.bf) return false;
      return s.bf.toLowerCase().replace(/\s+/g, '').includes(norm);
    });
  }

  /**
   * Compute the 3D position of a solar system body (Sun, Moon, Mercury,
   * Venus, Mars, Jupiter, Saturn, Uranus, Neptune, Pluto) as it appeared
   * from the observer's location at the observer's time.
   *
   * Uses the same horizontal-coordinate transform as the star catalog,
   * so the resulting position lines up correctly with the star field.
   *
   * @param {string} bodyName - e.g. 'Jupiter'
   * @returns {THREE.Vector3 | null}
   */
  getPlanetPosition(bodyName) {
    const body = Astronomy.Body[bodyName];
    if (!body) {
      console.warn(`StarField.getPlanetPosition: unknown body "${bodyName}"`);
      return null;
    }
    // Equatorial coordinates (apparent, with aberration) for the body
    // as seen by the observer at this.time
    const eq = Astronomy.Equator(body, this.time, this.observer, true, true);
    return this.getStarPosition({ ra: eq.ra, dec: eq.dec });
  }

  /**
   * Get the closest cataloged star to a target distance in light-years.
   * Used to pick the Birth Star (target = 28 ly).
   */
  findClosestToDistance(targetLightYears) {
    const targetParsecs = targetLightYears / 3.262;
    let best = null;
    let bestDelta = Infinity;
    for (const s of this.catalog) {
      const delta = Math.abs(s.d - targetParsecs);
      if (delta < bestDelta) {
        best = s;
        bestDelta = delta;
      }
    }
    return best;
  }

  update(_dt) {
    // Static for now. Subtle twinkle could go here later.
  }
}

/**
 * Convert B-V color index to approximate RGB.
 * Common astronomy formula. Returns values in [0, 1].
 */
function bvToRgb(bv) {
  if (bv < -0.4) bv = -0.4;
  if (bv > 2.0) bv = 2.0;

  let r, g, b;

  if (bv < 0.0) {
    const t = (bv + 0.4) / 0.4;
    r = 0.61 + 0.11 * t + 0.1 * t * t;
    g = 0.70 + 0.07 * t + 0.1 * t * t;
    b = 1.0;
  } else if (bv < 0.4) {
    r = 0.83 + 0.17 * (bv / 0.4);
    g = 0.87 + 0.11 * (bv / 0.4);
    b = 1.0;
  } else if (bv < 1.6) {
    r = 1.0;
    g = 0.98 - 0.16 * (bv - 0.4);
    b = 1.0 - 0.47 * (bv - 0.4);
  } else {
    r = 1.0;
    g = 0.82 - 0.5 * (bv - 1.6);
    b = 0.63 - 0.6 * (bv - 1.6);
  }

  return [
    Math.max(0, Math.min(1, r)),
    Math.max(0, Math.min(1, g)),
    Math.max(0, Math.min(1, b)),
  ];
}

/**
 * Convert apparent magnitude to point size.
 * Brighter (lower magnitude) = bigger.
 */
function magToSize(mag) {
  const v = 10 - mag * 1.1;
  return Math.max(1.5, Math.min(16, v));
}
